import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PortalStatus from '@/components/portal/PortalStatus';
import Toast from '@/components/ui/Toast';
import { shortDate } from '@/lib/format';
import {
  portalDiff,
  portalUploadHistory,
  portalUploadItems,
  type PortalPriceItem,
} from '@/mocks/portal';

type Phase = 'idle' | 'parsing' | 'review' | 'diff' | 'done';

const TOTAL_PAGES = 12;
const NEXT_VERSION = 'v13';

export default function PortalUploadPage() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [page, setPage] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [items, setItems] = useState<PortalPriceItem[]>(portalUploadItems);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [history, setHistory] = useState(portalUploadHistory);
  const [toast, setToast] = useState<{ message: string; detail?: string } | null>(null);

  useEffect(() => {
    if (phase !== 'parsing') return;
    if (page >= TOTAL_PAGES) {
      const timer = window.setTimeout(() => setPhase('review'), 420);
      return () => window.clearTimeout(timer);
    }
    const timer = window.setTimeout(() => setPage((p) => p + 1), 250);
    return () => window.clearTimeout(timer);
  }, [phase, page]);

  const recognised = Math.min(items.length, Math.round((page / TOTAL_PAGES) * items.length));
  const lowCount = items.filter((i) => i.confidence === 'low').length;

  const diffCounts = useMemo(
    () => ({
      new: portalDiff.filter((d) => d.type === 'new').length,
      removed: portalDiff.filter((d) => d.type === 'removed').length,
      price: portalDiff.filter((d) => d.type === 'price').length,
      stock: portalDiff.filter((d) => d.type === 'stock').length,
    }),
    [],
  );

  const patchItem = (sku: string, change: Partial<PortalPriceItem>) => {
    setItems((prev) => prev.map((i) => (i.sku === sku ? { ...i, ...change, confidence: 'high' } : i)));
  };

  const start = () => {
    setPage(1);
    setPhase('parsing');
  };

  const publish = () => {
    setConfirmOpen(false);
    setPhase('done');
    setHistory((prev) => [
      { version: NEXT_VERSION, date: '2026-10-24', items: items.length, status: 'Active', changes: '+2 / −1 / 5' },
      ...prev.map((h) => ({ ...h, status: 'Superseded' })),
    ]);
    setToast({ message: t('portal.upload.publishedToast', { version: NEXT_VERSION, count: items.length }) });
  };

  const reset = () => {
    setPhase('idle');
    setPage(1);
    setItems(portalUploadItems);
  };

  const steps: { key: Phase; label: string }[] = [
    { key: 'idle', label: t('portal.upload.stepUpload') },
    { key: 'review', label: t('portal.upload.stepReview') },
    { key: 'diff', label: t('portal.upload.stepDiff') },
    { key: 'done', label: t('portal.upload.stepDone') },
  ];
  const currentStep = phase === 'parsing' ? 0 : steps.findIndex((s) => s.key === phase);

  const confidenceTone = (c: PortalPriceItem['confidence']) =>
    c === 'high'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : c === 'medium'
        ? 'border-accent-200 bg-accent-50 text-accent-800'
        : 'border-rose-200 bg-rose-50 text-rose-700';

  return (
    <div>
      <div className="mb-4 border-b border-background-200 pb-4">
        <h1 className="text-[20px] font-semibold leading-6 text-foreground-950">{t('portal.upload.title')}</h1>
        <p className="mt-1 max-w-3xl text-[12.5px] leading-5 text-foreground-500">{t('portal.upload.subtitle')}</p>
      </div>

      <ol className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        {steps.map((s, index) => {
          const active = index === currentStep;
          const done = index < currentStep;
          return (
            <li
              key={s.label}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 ${
                active
                  ? 'border-primary-300 bg-primary-50'
                  : done
                    ? 'border-background-200 bg-background-50'
                    : 'border-background-200 bg-background-100'
              }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                  done
                    ? 'bg-primary-500 text-background-50'
                    : active
                      ? 'bg-primary-600 text-background-50'
                      : 'bg-background-300 text-foreground-600'
                }`}
              >
                {done ? <i className="ri-check-line text-[11px]" /> : index + 1}
              </span>
              <span
                className={`truncate text-[11.5px] ${
                  active ? 'font-semibold text-primary-900' : 'font-medium text-foreground-600'
                }`}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>

      {phase === 'idle' && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="xl:col-span-2">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                start();
              }}
              className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-14 text-center transition-colors ${
                dragging ? 'border-primary-400 bg-primary-50' : 'border-background-300 bg-background-50'
              }`}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-700">
                <i className="ri-upload-cloud-2-line text-[24px]" />
              </span>
              <p className="mt-3 text-[14px] font-semibold text-foreground-950">{t('portal.upload.drop')}</p>
              <p className="mt-1 text-[11.5px] text-foreground-500">{t('portal.upload.dropSub')}</p>
              <button
                type="button"
                onClick={start}
                className="mt-4 inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 text-[12.5px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-file-text-line text-[14px]" />
                {t('portal.upload.browse')}
              </button>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
                {['PDF', 'XLSX', 'CSV', 'JPG', 'PNG'].map((f) => (
                  <span
                    key={f}
                    className="rounded border border-background-200 bg-background-100 px-1.5 py-0.5 text-[9.5px] font-semibold tracking-wide text-foreground-500"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-[12px] font-semibold text-foreground-950">{t('portal.upload.formats')}</p>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {[
                  { icon: 'ri-file-pdf-2-line', label: 'PDF' },
                  { icon: 'ri-file-excel-2-line', label: 'Excel' },
                  { icon: 'ri-smartphone-line', label: 'Photos' },
                ].map((f) => (
                  <div key={f.label} className="flex items-center gap-2 rounded border border-background-200 px-2.5 py-2">
                    <i className={`${f.icon} text-[15px] text-primary-600`} />
                    <span className="text-[11.5px] font-medium text-foreground-700">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[12px] font-semibold text-foreground-950">{t('portal.upload.historyTitle')}</p>
                <p className="mt-0.5 text-[10.5px] leading-4 text-foreground-500">{t('portal.upload.historySub')}</p>
              </div>
              <i className="ri-history-line text-[15px] text-foreground-300" />
            </div>
            <ul className="mt-3 divide-y divide-background-200/70">
              {history.map((h) => (
                <li key={h.version} className="flex items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-[11.5px] font-semibold text-foreground-900">
                      {h.version}
                      {h.status === 'Active' && (
                        <span className="rounded-full bg-primary-50 px-1.5 text-[9.5px] font-semibold text-primary-700">
                          {t('portal.upload.active')}
                        </span>
                      )}
                    </p>
                    <p className="tnum text-[10px] text-foreground-400">
                      {shortDate(h.date)} · {h.items} {t('portal.common.items')}
                    </p>
                  </div>
                  <span className="tnum text-[10px] text-foreground-500">{h.changes}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {phase === 'parsing' && (
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                <i className="ri-file-excel-2-line text-[19px]" />
              </span>
              <div>
                <p className="text-[13px] font-semibold text-foreground-950">
                  {t('portal.upload.fileChosen', { name: t('portal.upload.fileName'), pages: TOTAL_PAGES })}
                </p>
                <p className="tnum mt-0.5 text-[11px] text-foreground-500">
                  {t('portal.upload.parsingSub', { page, total: TOTAL_PAGES, count: recognised })}
                </p>
              </div>
            </div>
            <span className="flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-[10.5px] font-semibold text-primary-800">
              <i className="ri-loader-4-line animate-spin text-[12px]" />
              {t('portal.upload.parsing')}
            </span>
          </div>

          <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-300"
              style={{ width: `${Math.round((page / TOTAL_PAGES) * 100)}%` }}
            />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              { label: t('portal.upload.colItems'), value: recognised },
              { label: t('portal.upload.colStock'), value: Math.round(recognised * 0.9) },
              { label: t('portal.upload.stepReview'), value: Math.max(0, recognised - 2) },
              { label: t('portal.upload.lowRowsLabel'), value: lowCount },
            ].map((s) => (
              <div key={s.label} className="rounded border border-background-200 bg-background-100 px-2.5 py-2">
                <p className="text-[9.5px] uppercase tracking-[0.08em] text-foreground-500">{s.label}</p>
                <p className="tnum mt-0.5 text-[15px] font-semibold text-foreground-900">{s.value}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[10.5px] text-foreground-400">{t('portal.upload.parsingNote')}</p>
        </div>
      )}

      {phase === 'review' && (
        <div className="rounded-lg border border-background-200 bg-background-50">
          <div className="flex flex-col gap-2 border-b border-background-200 px-4 py-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.upload.reviewTitle')}</h2>
              <p className="mt-0.5 text-[11px] text-foreground-500">{t('portal.upload.reviewSub')}</p>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="rounded-full border border-background-200 px-2 py-0.5 text-[10.5px] text-foreground-600">
                {t('portal.upload.recognized', { count: items.length })}
              </span>
              <span className="rounded-full border border-accent-200 bg-accent-50 px-2 py-0.5 text-[10.5px] font-medium text-accent-800">
                {t('portal.upload.lowHint', { count: lowCount })}
              </span>
            </div>
          </div>

          <div className="overflow-x-auto thin-scroll">
            <table className="w-full min-w-[860px] border-collapse text-[12px]">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                  <th className="px-3 py-2.5 font-medium">{t('portal.upload.colItem')}</th>
                  <th className="px-2 py-2.5 font-medium">{t('portal.common.sku')}</th>
                  <th className="px-2 py-2.5 font-medium">{t('portal.upload.colPrice')}</th>
                  <th className="px-2 py-2.5 font-medium">{t('portal.upload.colStock')}</th>
                  <th className="px-2 py-2.5 font-medium">{t('portal.upload.colIncoming')}</th>
                  <th className="px-2 py-2.5 font-medium">{t('portal.upload.colEta')}</th>
                  <th className="px-3 py-2.5 font-medium">{t('portal.upload.colConfidence')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => (
                  <tr
                    key={i.sku}
                    className={`border-b border-background-200/70 last:border-0 ${
                      i.confidence === 'low'
                        ? 'bg-accent-50/60'
                        : i.confidence === 'medium'
                          ? 'bg-background-100/40'
                          : ''
                    }`}
                  >
                    <td className="max-w-[240px] px-3 py-2">
                      <p className="truncate text-[12px] font-medium text-foreground-900">{i.name}</p>
                    </td>
                    <td className="tnum px-2 py-2 text-[11px] font-semibold text-primary-700">{i.sku}</td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-foreground-400">AED</span>
                        <input
                          type="number"
                          value={i.price}
                          onChange={(e) => patchItem(i.sku, { price: Number(e.target.value) || 0 })}
                          aria-label={`${i.sku} ${t('portal.upload.colPrice')}`}
                          className="tnum h-6 w-[70px] rounded border border-background-300 bg-background-50 px-1.5 text-[11.5px] outline-none focus:border-primary-400"
                        />
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        value={i.stock}
                        onChange={(e) => patchItem(i.sku, { stock: Number(e.target.value) || 0 })}
                        aria-label={`${i.sku} ${t('portal.upload.colStock')}`}
                        className="tnum h-6 w-[64px] rounded border border-background-300 bg-background-50 px-1.5 text-[11.5px] outline-none focus:border-primary-400"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        value={i.incoming}
                        onChange={(e) => patchItem(i.sku, { incoming: Number(e.target.value) || 0 })}
                        aria-label={`${i.sku} ${t('portal.upload.colIncoming')}`}
                        className="tnum h-6 w-[64px] rounded border border-background-300 bg-background-50 px-1.5 text-[11.5px] outline-none focus:border-primary-400"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="date"
                        value={i.eta}
                        onChange={(e) => patchItem(i.sku, { eta: e.target.value })}
                        aria-label={`${i.sku} ${t('portal.upload.colEta')}`}
                        className="h-6 w-[110px] rounded border border-background-300 bg-background-50 px-1 text-[10.5px] outline-none focus:border-primary-400"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${confidenceTone(i.confidence)}`}
                      >
                        <i
                          className={`${
                            i.confidence === 'high'
                              ? 'ri-checkbox-circle-line'
                              : i.confidence === 'medium'
                                ? 'ri-question-line'
                                : 'ri-alert-line'
                          } text-[10px]`}
                        />
                        {i.confidence === 'high'
                          ? t('portal.upload.confidenceHigh')
                          : i.confidence === 'medium'
                            ? t('portal.upload.confidenceMedium')
                            : t('portal.upload.confidenceLow')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-2 border-t border-background-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={reset}
              className="cursor-pointer whitespace-nowrap text-left text-[11.5px] font-medium text-foreground-500 hover:text-foreground-800"
            >
              {t('portal.upload.reset')}
            </button>
            <button
              type="button"
              onClick={() => setPhase('diff')}
              className="inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
            >
              {t('portal.upload.confirmItems')}
              <i className="ri-arrow-right-line text-[13px]" />
            </button>
          </div>
        </div>
      )}

      {phase === 'diff' && (
        <div className="rounded-lg border border-background-200 bg-background-50">
          <div className="border-b border-background-200 px-4 py-3">
            <h2 className="text-[13px] font-semibold text-foreground-950">
              {t('portal.upload.diffTitle', { version: 'v12' })}
            </h2>
            <p className="mt-0.5 text-[11px] text-foreground-500">{t('portal.upload.diffSub')}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 p-3 md:grid-cols-4">
            {[
              { label: t('portal.upload.diffNew'), value: diffCounts.new, icon: 'ri-add-circle-line', tone: 'text-emerald-700' },
              { label: t('portal.upload.diffRemoved'), value: diffCounts.removed, icon: 'ri-indeterminate-circle-line', tone: 'text-rose-600' },
              { label: t('portal.upload.diffPrice'), value: diffCounts.price, icon: 'ri-price-tag-3-line', tone: 'text-primary-700' },
              { label: t('portal.upload.diffStock'), value: diffCounts.stock, icon: 'ri-archive-2-line', tone: 'text-sky-700' },
            ].map((s) => (
              <div key={s.label} className="rounded-md border border-background-200 bg-background-100 px-3 py-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-[0.08em] text-foreground-500">{s.label}</p>
                  <i className={`${s.icon} text-[13px] ${s.tone}`} />
                </div>
                <p className="tnum mt-0.5 text-[17px] font-semibold text-foreground-950">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto thin-scroll">
            <table className="w-full min-w-[720px] border-collapse text-[12px]">
              <thead>
                <tr className="border-y border-background-200 bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                  <th className="px-4 py-2 font-medium">{t('portal.upload.colChange')}</th>
                  <th className="px-3 py-2 font-medium">{t('portal.common.sku')}</th>
                  <th className="px-3 py-2 font-medium">{t('portal.upload.colItem')}</th>
                  <th className="px-3 py-2 font-medium">{t('portal.upload.colWas')}</th>
                  <th className="px-4 py-2 font-medium">{t('portal.upload.colNow')}</th>
                </tr>
              </thead>
              <tbody>
                {portalDiff.map((d) => (
                  <tr key={`${d.type}-${d.sku}`} className="border-b border-background-200/70 last:border-0">
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${
                          d.type === 'new'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : d.type === 'removed'
                              ? 'border-rose-200 bg-rose-50 text-rose-700'
                              : d.type === 'price'
                                ? 'border-primary-200 bg-primary-50 text-primary-800'
                                : 'border-sky-200 bg-sky-50 text-sky-800'
                        }`}
                      >
                        {d.type === 'new'
                          ? t('portal.upload.diffNew')
                          : d.type === 'removed'
                            ? t('portal.upload.diffRemoved')
                            : d.type === 'price'
                              ? t('portal.upload.diffPrice')
                              : t('portal.upload.diffStock')}
                      </span>
                    </td>
                    <td className="tnum px-3 py-2 text-[11px] font-semibold text-primary-700">{d.sku}</td>
                    <td className="max-w-[220px] truncate px-3 py-2 text-foreground-800">{d.name}</td>
                    <td className="tnum px-3 py-2 text-[11.5px] text-foreground-500">{d.was}</td>
                    <td className="tnum px-4 py-2 text-[11.5px] font-medium text-foreground-900">{d.now}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-2 border-t border-background-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => setPhase('review')}
              className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-[11.5px] font-medium text-foreground-600 hover:text-foreground-900"
            >
              <i className="ri-arrow-left-line text-[12px]" />
              {t('portal.common.back')}
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-upload-cloud-2-line text-[13px]" />
              {t('portal.upload.publish')}
            </button>
          </div>
        </div>
      )}

      {phase === 'done' && (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <i className="ri-checkbox-circle-fill text-[22px]" />
            </span>
            <div className="min-w-0">
              <h2 className="text-[15px] font-semibold text-foreground-950">
                {t('portal.upload.stepDone')} · {NEXT_VERSION}
              </h2>
              <p className="mt-1 max-w-2xl text-[12px] leading-5 text-foreground-500">
                {t('portal.upload.publishSub', { count: items.length })}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
                >
                  <i className="ri-upload-2-line text-[13px]" />
                  {t('portal.upload.uploadNew')}
                </button>
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100"
                >
                  <i className="ri-history-line text-[13px]" />
                  {t('portal.upload.historyTitle')}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2 md:grid-cols-4">
            {[
              { label: t('portal.upload.diffNew'), value: diffCounts.new },
              { label: t('portal.upload.diffRemoved'), value: diffCounts.removed },
              { label: t('portal.upload.diffPrice'), value: diffCounts.price },
              { label: t('portal.upload.diffStock'), value: diffCounts.stock },
            ].map((s) => (
              <div key={s.label} className="rounded-md border border-background-200 bg-background-100 px-3 py-2">
                <p className="text-[9.5px] uppercase tracking-[0.08em] text-foreground-500">{s.label}</p>
                <p className="tnum mt-0.5 text-[16px] font-semibold text-foreground-900">{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-200 px-4 py-2.5">
          <p className="text-[11.5px] font-semibold text-foreground-900">{t('portal.upload.historyTitle')}</p>
          <span className="text-[10.5px] text-foreground-400">{t('portal.upload.historySub')}</span>
        </div>
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[620px] border-collapse text-[12px]">
            <thead>
              <tr className="bg-background-100/70 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2 font-medium">{t('portal.upload.colVersion')}</th>
                <th className="px-3 py-2 font-medium">{t('portal.upload.colDate')}</th>
                <th className="px-3 py-2 font-medium">{t('portal.upload.colItems')}</th>
                <th className="px-3 py-2 font-medium">{t('portal.upload.colChanges')}</th>
                <th className="px-4 py-2 font-medium">{t('portal.upload.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.version} className="border-t border-background-200/70">
                  <td className="px-4 py-2 font-semibold text-foreground-900">{h.version}</td>
                  <td className="tnum px-3 py-2 text-foreground-600">{shortDate(h.date)}</td>
                  <td className="tnum px-3 py-2 text-foreground-600">{h.items}</td>
                  <td className="tnum px-3 py-2 text-foreground-600">{h.changes}</td>
                  <td className="px-4 py-2">
                    <PortalStatus
                      status={h.status === 'Active' ? 'Published' : 'Closed'}
                      label={h.status === 'Active' ? t('portal.upload.active') : t('portal.upload.superseded')}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/30 p-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-[14px] font-semibold text-foreground-950">
              {t('portal.upload.publishTitle', { version: NEXT_VERSION })}
            </h2>
            <p className="mt-1.5 text-[12px] leading-5 text-foreground-500">
              {t('portal.upload.publishSub', { count: items.length })}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100"
              >
                {t('portal.common.cancel')}
              </button>
              <button
                type="button"
                onClick={publish}
                className="h-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
              >
                {t('portal.upload.publishConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} detail={toast.detail} onClose={() => setToast(null)} />}
      <p className="mt-3 text-[10.5px] text-foreground-400">{t('portal.products.priceNote')}</p>
    </div>
  );
}